import React, { useState, memo, useCallback } from "react";
import { useApp } from "@/contexts/AppContext";
import { ChevronRight, ChevronDown } from "lucide-react";

function SevBadge({ sev = 0, total = 0 }) {
  return (
    <span className="text-[10px] text-white/60 flex items-center gap-1">
      <span className="w-1.5 h-1.5 bg-[var(--sev-critical)]" />
      <span className="font-display">{sev}</span>
      <span className="text-white/30">/ {total}</span>
    </span>
  );
}

const pctCalc = (c, t) => (t > 0 ? Math.min(100, (c / t) * 100) : 0);

const Row = memo(function Row({ label, signals, critical, indent, onClick, children, openable, testid, sub }) {
  const [open, setOpen] = useState(false);
  const pct = pctCalc(critical, signals);
  
  // Steel-clad label filtering: Never show empty brackets
  let cleanLabel = String(label || "").trim();
  if (!cleanLabel || cleanLabel === "[]" || cleanLabel === "['']") {
    cleanLabel = "General Operational";
  }

  const handleToggle = useCallback(() => {
    if (openable) setOpen(prev => !prev);
  }, [openable]);

  return (
    <div data-testid={testid}>
      <div
        className="flex items-center gap-1.5 py-1 pr-2 hover:bg-blue-50/50 border-b border-gray-100 group transition-colors"
        style={{ paddingLeft: 6 + indent * 10 }}
      >
        <div 
          className="flex items-center gap-1.5 flex-1 min-w-0 cursor-pointer"
          onClick={handleToggle}
        >
          {openable ? (
            open ? <ChevronDown className="w-3 h-3 text-gray-400" /> : <ChevronRight className="w-3 h-3 text-gray-400" />
          ) : <span className="w-3" />}
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-medium text-gray-800 truncate">{cleanLabel}</div>
            {sub && <div className="text-[8px] uppercase tracking-wider text-gray-400 truncate">{sub}</div>}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="w-12 h-1 bg-gray-100 rounded-full relative overflow-hidden">
            <div className="h-full bg-[var(--sev-critical)]" style={{ width: `${pct}%` }} />
          </div>
          <span className="text-[9px] text-gray-500 font-mono">
            <span className="text-red-600 font-bold">{critical}</span>
            <span className="opacity-40"> / {signals}</span>
          </span>
          <button 
            onClick={onClick}
            className="text-[8px] font-bold uppercase tracking-widest text-blue-600 hover:text-blue-800 opacity-0 group-hover:opacity-100 transition-opacity px-0.5"
          >
            Intel →
          </button>
        </div>
      </div>
      {open && children}
    </div>
  );
});

export default memo(function HierarchyTree() {
  const { hierarchy, setSelectedDetail } = useApp();

  const handleDrill = useCallback((type, payload) => {
    React.startTransition(() => {
      setSelectedDetail({ type, payload });
    });
  }, [setSelectedDetail]);

  return (
    <div data-testid="hierarchy-tree" className="max-h-[560px] overflow-y-auto custom-scrollbar">
      {(!hierarchy || hierarchy.length === 0) && (
        <div className="text-xs text-white/50 p-3">No hierarchy data available.</div>
      )}
      {hierarchy.map((v) => (
        <Row
          key={v.name}
          testid={`hier-venture-${v.name}`}
          label={v.name}
          signals={v.signals}
          critical={v.critical}
          indent={0}
          openable
          onClick={() => handleDrill("venture", v)}
        >
          {v.sites.map((s) => (
            <Row
              key={`${v.name}-${s.name}`}
              testid={`hier-site-${s.name}`}
              label={s.name}
              sub={s.state}
              signals={s.signals}
              critical={s.critical}
              indent={1}
              openable={s.categories?.some((c) => c.critical > 0)}
              onClick={() => handleDrill("site", { ...s, venture: v.name })}
            >
              {s.categories
                .filter((c) => c.critical > 0)
                .map((c) => (
                  <Row
                    key={`${v.name}-${s.name}-${c.name}`}
                    testid={`hier-cat-${c.name}`}
                    label={c.name}
                    signals={c.signals}
                    critical={c.critical}
                    indent={2}
                    onClick={() => handleDrill("category-site", { ...c, venture: v.name, site: s.name })}
                  />
                ))}
            </Row>
          ))}
        </Row>
      ))}
    </div>
  );
});
